import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import useMarketLandscapeTab from '../../../MarketLandscapeTab/useMarketLandscapeTab.logic';
import GeneratingLoader from 'components/generatingLoader/generatingLoader';
import './MarketInsights.styles.scss';

const MarketInsights: React.FC = observer(() => {
  const { aiInsights, isInsightsLoading, insightsError } = useMarketLandscapeTab(productAnalysisStore);
  
  // Track if we've ever received data or completed loading (to differentiate initial load from "no data")
  const hasCompletedInitialLoadRef = useRef<boolean>(false);
  
  // Update ref when we receive data or finish loading with error
  if (aiInsights !== null || insightsError !== null) {
    hasCompletedInitialLoadRef.current = true;
  }

  // Show loader during loading OR during initial state (before first load completes)
  const shouldShowLoader = isInsightsLoading || (!hasCompletedInitialLoadRef.current && !insightsError);

  if (shouldShowLoader) {
    return (
      <section className="market-insights-section">
        <div className="narrative-box ai-insights-box">
          <h4 className="narrative-box-text-title">Market Opportunity Insights</h4>
          <div className="narrative-content">
            <GeneratingLoader />
          </div>
        </div>
      </section>
    );
  }

  if (insightsError) {
    return (
      <section className="market-insights-section">
        <div className="narrative-box ai-insights-box error">
          <h4 className="narrative-box-text-title">Market Opportunity Insights</h4>
          <div className="narrative-content">
            <div className="insights-error-container">
              <i className="fas fa-exclamation-triangle insights-error-icon" />
              <div className="insights-error-details">
                <p className="insights-error-message">{insightsError}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (aiInsights && aiInsights.length > 0) {
    return (
      <section className="market-insights-section">
        <div className="narrative-box ai-insights-box">
          <h4 className="narrative-box-text-title">Market Opportunity Insights</h4>
          <div className="narrative-content">
            <ul className="ai-insights-list">
              {aiInsights.map((insight, index) => (
                <li key={index} className="insight-item">
                  <span className="narrative-box-text-item">{insight.bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }

  // Only show "no data" after initial load has completed
  return (
    <section className="market-insights-section">
      <div className="narrative-box ai-insights-box no-data">
        <h4 className="narrative-box-text-title">Market Opportunity Insights</h4>
        <div className="narrative-content">
          <div className="insights-no-data">
            <span className="no-data-icon">📊</span>
            <p className="no-data-message">No AI insights available at the moment.</p>
            <p className="no-data-hint">
              Insights will be generated based on market data analysis.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
});

MarketInsights.displayName = 'MarketInsights';

export default MarketInsights;
